import React from 'react';
import { Store, Award, Bell, RefreshCw, CheckCircle } from 'lucide-react';

const s = {
  page: { padding: '2rem', maxWidth: 1200, margin: '0 auto' },
  h1: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.75rem', marginBottom: 4 },
  desc: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.9rem', marginBottom: '2rem', maxWidth: 700, lineHeight: 1.6 },
  card: { background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem' },
  cardTitle: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 10 },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' },
  label: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.8rem', marginBottom: 2 },
  value: { fontFamily: 'DM Sans, sans-serif', color: 'var(--text)', fontSize: '0.95rem' },
  item: { padding: '8px 0', borderBottom: '1px solid rgba(242,237,228,0.06)' },
  storeGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  storeCard: { background: 'var(--bg)', borderRadius: 8, padding: '1rem' },
  storeName: { fontFamily: 'DM Sans, sans-serif', color: 'var(--text)', fontSize: '0.95rem', fontWeight: 600, marginBottom: 2 },
  storeAddr: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.82rem' },
  tierRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(242,237,228,0.06)' },
  tierName: { fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.95rem' },
  tierThreshold: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.85rem' },
  tierDot: (color) => ({ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: color, marginRight: 8 }),
  statRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', fontFamily: 'DM Sans, sans-serif', color: 'var(--text)', fontSize: '0.9rem' },
  healthy: { color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: '0.85rem' },
};

const stores = [
  { name: 'Wicker Park', address: '1438 N Milwaukee Ave, Chicago, IL 60622' },
  { name: 'Lincoln Park', address: '2135 N Halsted St, Chicago, IL 60614' },
  { name: 'River North', address: '430 N Clark St, Chicago, IL 60654' },
  { name: 'West Loop', address: '815 W Randolph St, Chicago, IL 60607' },
  { name: 'Evanston', address: '524 Davis St, Evanston, IL 60201' },
  { name: 'Oak Park', address: '110 N Marion St, Oak Park, IL 60301' },
];

const tiers = [
  { name: 'Seedling', threshold: '$0 - $599/yr', color: 'var(--secondary)', pct: '55%' },
  { name: 'Bloom', threshold: '$600 - $1,799/yr', color: 'var(--primary)', pct: '30%' },
  { name: 'Harvest', threshold: '$1,800+/yr', color: 'var(--alert)', pct: '15%' },
];

export default function Settings() {
  return (
    <div style={s.page}>
      <h1 style={s.h1}>Settings</h1>
      <p style={s.desc}>Client configuration and integration status for Folio Coffee Co.</p>

      <div style={s.card}>
        <div style={s.cardTitle}><Store size={18} /> Client Information</div>
        <div style={s.row}>
          <div>
            <div style={s.label}>Business Name</div>
            <div style={s.value}>Folio Coffee Co.</div>
          </div>
          <div>
            <div style={s.label}>Tagline</div>
            <div style={{ ...s.value, fontStyle: 'italic' }}>Brewed with intention.</div>
          </div>
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}><Store size={18} /> Store Locations</div>
        <div style={s.storeGrid}>
          {stores.map((store) => (
            <div key={store.name} style={s.storeCard}>
              <div style={s.storeName}>{store.name}</div>
              <div style={s.storeAddr}>{store.address}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={s.row}>
        <div style={s.card}>
          <div style={s.cardTitle}><Award size={18} /> Loyalty Tiers</div>
          {tiers.map((tier) => (
            <div key={tier.name} style={s.tierRow}>
              <div>
                <span style={s.tierDot(tier.color)} />
                <span style={{ ...s.tierName, color: tier.color }}>{tier.name}</span>
              </div>
              <div style={s.tierThreshold}>{tier.threshold} ({tier.pct})</div>
            </div>
          ))}
        </div>

        <div style={s.card}>
          <div style={s.cardTitle}><Bell size={18} /> Push Configuration</div>
          <div style={s.item}>
            <div style={s.label}>Provider</div>
            <div style={s.value}>Klaviyo Push</div>
          </div>
          <div style={s.item}>
            <div style={s.label}>Opt-in Rate</div>
            <div style={s.value}>45%</div>
          </div>
          <div style={s.item}>
            <div style={s.label}>Avg CTR</div>
            <div style={s.value}>11%</div>
          </div>
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}><RefreshCw size={18} /> Data Sync Status</div>
        <div style={s.statRow}>
          <span>Toast POS → Hahn AI</span>
          <span style={s.healthy}><CheckCircle size={14} /> Healthy</span>
        </div>
        <div style={s.statRow}>
          <span>Klaviyo → Hahn AI</span>
          <span style={s.healthy}><CheckCircle size={14} /> Healthy</span>
        </div>
        <div style={s.statRow}>
          <span>Hahn AI → Klaviyo (segments)</span>
          <span style={s.healthy}><CheckCircle size={14} /> Healthy</span>
        </div>
        <div style={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.8rem', marginTop: '0.75rem' }}>
          Last sync: 12 minutes ago
        </div>
      </div>
    </div>
  );
}
